import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@mui/styles";

import { DEPOT_SELECT } from "../../consts/sentryIds";
import { PLEASE_SELECT } from "../../consts/strings";
import { palette } from "../../theme/palette";

import { depotLoadingSelector } from "../../reducers/depot/selectors";
import { getDepotsForDropDown } from "../../reducers/ownUser/selectors";
import { getSelectedDepot } from "../../reducers/ownApp/selectors";
import { setSelectedDepot } from "../../reducers/ownApp/thunkActions";
import { isEditModeSelector } from "../../reducers/matrixProfile/selectors";

import { useLocationDetails } from "../../hooks/useLocationDetails";

import { Dropdown } from "../Dropdown";
import { HeaderBar } from "./HeaderBar";
import { PlanSelector } from "./PlanSelector";
import { SearchBar } from "../SearchBar";
import { TypeSelector } from "./TypeSelector";
import { View } from "../View";
import VolumeOverlay from "./VolumeOverlay";
import AppMenu from "../AppMenu";

const useStyles = makeStyles(({ typography }) => ({
  container: {
    backgroundColor: palette.black,
  },
  space: {
    flex: 1,
    maxWidth: typography.pxToRem(50),
  },
  largeSpace: {
    flex: 1,
    maxWidth: typography.pxToRem(175),
  },
  menuSpace: {
    marginRight: typography.pxToRem(10),
  },
  divider: {
    color: palette.typography.white.solid,
  },
}));

const Header = ({ children }) => {
  const styles = useStyles();
  const depots = useSelector(getDepotsForDropDown);
  const selectedDepot = useSelector(getSelectedDepot);
  const depotLoading = useSelector(depotLoadingSelector);
  const isEditMode = useSelector(isEditModeSelector);
  const dispatch = useDispatch();
  const {
    application,
    isPlanManagement,
    isVolumeOverlayTable,
    params = { depotCode: null },
    removeParam,
  } = useLocationDetails();

  const handleDepotChange = useCallback(
    (depotNumber) => {
      dispatch(setSelectedDepot({ value: depotNumber, application }));
      removeParam("depotCode");
    },
    [dispatch, application, removeParam]
  );

  useEffect(() => {
    if (
      params.depotCode &&
      depots.find(({ value }) => value === params.depotCode)
    )
      handleDepotChange(params.depotCode);
  }, [selectedDepot, depots, params.depotCode, handleDepotChange]);

  return (
    <View className={styles.container}>
      <HeaderBar>
        {selectedDepot && !depotLoading && (
          <>
            {!isVolumeOverlayTable && <SearchBar application={application} />}
            <View className={styles.space} />
            {isPlanManagement ? (
              <PlanSelector />
            ) : (
              <View className={styles.largeSpace} />
            )}
            <View className={styles.space} />
            {isPlanManagement ? (
              <TypeSelector />
            ) : (
              <View className={styles.largeSpace} />
            )}
            <View className={styles.space} />
            {isPlanManagement || isVolumeOverlayTable ? (
              <VolumeOverlay />
            ) : (
              <View className={styles.largeSpace} />
            )}
          </>
        )}
        <View className={styles.space} />
        <Dropdown
          disabled={isEditMode}
          value={selectedDepot}
          items={depots}
          onChange={handleDepotChange}
          placeholder={PLEASE_SELECT}
          id={DEPOT_SELECT}
        />
        <View className={styles.space} />
        <AppMenu />
        <View className={styles.menuSpace} />
      </HeaderBar>
      {children}
    </View>
  );
};

export default Header;
